import React, { useContext, useState, useRef } from 'react';
import { ALERT_TYPE, CUENTA_STATE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, TRIBUTO_TYPE } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { ServerRequest } from '../../utils/apiweb';
import { useFilterPersistence, useNavigateWithFilters } from '../../components/hooks';
import { createSearchWithSavedFilters } from '../../utils/searchWithSavedFilters';
import {
    Loading,
    TableCustom,
    SectionHeading,
    WrapperAdvancedSearch
} from '../../components/common';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { ManagedContext } from '../../context/custom';
import { useLista } from '../../components/hooks/useLista';
import { usePermissions } from '../../components/hooks/usePermissions';
import { PERMISSIONS } from '../../consts/permissions';
import {TIPS} from "../../consts/tips";
import { parseSerializedIds } from '../../utils/convert';
import { buildInmuebleEntityInit } from '../InmuebleView/utils';

function InmueblesView() {

    //hooks
    const { showMessageModal } = useContext(ManagedContext);

    const [state, setState] = useState({
        loading: false,
        dataTagger: {
            showModal: false,
            idEntidad: null
        },
        list: []
    });
    const searchRef = useRef();
    const tableRef = useRef();
    const [tableVisible, setTableVisible] = useState(false);
    const [searchMessage, setSearchMessage] = useState(null);

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.INMUEBLE_EDIT);
    const canReactivar = hasPermission(PERMISSIONS.CUENTA_EDIT);

    const tabsConfig = [
        {
            key: 'cuenta',
            title: 'Cuenta',
            fields: ['numeroCuenta', 'idPersona', 'idEstadoCuenta', 'etiqueta', 'idsTipoCondicionEspecial', 'idsTipoRecargoDescuento'],
            buildUrl: (filters) =>
                `/cuenta/filter?` +
                `numeroCuenta=${encodeURIComponent(filters.numeroCuenta)}&` +
                `idEstadoCuenta=${filters.idEstadoCuenta}&` +
                `numeroWeb=&numeroDocumento=&` +
                `idPersona=${filters.idPersona}&` +
                `etiqueta=${encodeURIComponent(filters.etiqueta)}&` +
                `idsTipoCondicionEspecial=${encodeURIComponent(parseSerializedIds(filters.idsTipoCondicionEspecial))}&` +
                `idsTipoRecargoDescuento=${encodeURIComponent(parseSerializedIds(filters.idsTipoRecargoDescuento))}`
        },
        {
            key: 'ubicacion',
            title: 'Ubicación',
            fields: ['direccion', 'catastralRtasPrv', 'catastralPartido', 'catastralCir', 'catastralSec', 'catastralCodigo', 'catastralChacra', 'catastralLchacra',  
                    'catastralQuinta', 'catastralLquinta', 'catastralFrac', 'catastralLfrac', 'catastralManz', 'catastralLmanz', 'catastralParc', 'catastralLparc', 'catastralSubparc', 'catastralUfunc', 'catastralUcomp'],
            buildUrl: (filters) => {
                  let catastral = [
                    filters.catastralPartido,
                    filters.catastralCir,
                    filters.catastralSec,
                    filters.catastralCodigo,
                    filters.catastralChacra,
                    filters.catastralLchacra,
                    filters.catastralQuinta,
                    filters.catastralLquinta,
                    filters.catastralFrac,
                    filters.catastralLfrac,
                    filters.catastralManz,
                    filters.catastralLmanz,
                    filters.catastralParc,
                    filters.catastralLparc,
                    filters.catastralSubparc,
                    filters.catastralUfunc,
                    filters.catastralUcomp
                ]
                .map(v => v || '%')
                .join('|');
            
                return `/ubicacion/filter?` +
                `catastral=${encodeURIComponent(catastral)}&` +
                `direccion=${encodeURIComponent(filters.direccion)}&` +
                `catastralRtasPrv=${encodeURIComponent(filters.catastralRtasPrv)}`
            }
        }
    ];

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
            .then((error) => {
                const message = error.message;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => ({ ...prevState, loading: false }));
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => ({ ...prevState, loading: false }));
            });
    };
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => ({ ...prevState, loading: false }));
    };

    const SearchInmueblesWithSavedFilters = createSearchWithSavedFilters({
        setState,
        setTableVisible,
        apiUrl: APIS.URLS.INMUEBLE,
        tabsConfig,
        callbackNoSuccess,
        callbackError,
        setSearchMessage
    });

    const { getCurrentFilters, restoredPageIndex } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchInmueblesWithSavedFilters
    );
    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters);

    //definiciones

    const cellA = (props) => !canEdit ? '' : (
        <div className='action'>
            <div onClick={() => handleClickInmuebleAdd()} className="link">
                <span className="material-symbols-outlined" title="Nuevo">add</span>
            </div>
        </div>
    );

    const cellVMR = (props) => (
        <div className='action'>
            <div onClick={() => handleClickInmuebleView(props.value)} className="link">
                <span className="material-symbols-outlined" title="Ver">search</span>
            </div>
            {canEdit && props.row.original.idEstadoCuenta !== CUENTA_STATE.INACTIVA && (
                <>
                    <div onClick={() => handleClickInmuebleModify(props.value)} className="link">
                        <span className="material-symbols-outlined" title="Modificar">stylus</span>
                    </div>
                    <div onClick={() => handleClickInmuebleRemove(props.value)} className="link">
                        <span className="material-symbols-outlined" title="Eliminar">delete</span>
                    </div>
                </>
            )}
            {canReactivar && props.row.original.idEstadoCuenta === CUENTA_STATE.INACTIVA && (
                <div onClick={() => handleClickReactivar(props.row.original.idCuenta, props.row.original.numeroCuenta)} className="link">
                    <span className="material-symbols-outlined" title="Reactivar">unarchive</span>
                </div>
            )}
            <div onClick={() => handleClickInmuebleDataTagger(props.value)} className="link">
                <span className="material-symbols-outlined" title="Información adicional">info</span>
            </div>
        </div>
    );

    const [, getRowLista] = useLista({
        listas: ['TipoDocumento', 'EstadoCuenta'],
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: {
            key: 'TipoDocumento_EstadoCuenta',
            timeout: 0
        }
    });

    const getDescEstadoCuenta = (id) => {
        const row = getRowLista('EstadoCuenta', id);
        return (row) ? row.nombre : '';
    };

    const tableColumns = [
        { Header: 'N° cuenta', accessor: 'numeroCuenta', width: '20%' },
        { Header: 'Dirección', accessor: 'direccion', width: '40%' },
        {
            Header: 'Estado cuenta',
            Cell: (props) => getDescEstadoCuenta(props.value),
            accessor: 'idEstadoCuenta',
            width: '20%'
        },
        {
            Header: cellA,
            Cell: cellVMR,
            id: 'abm',
            accessor: 'id',
            width: '10%',
            disableGlobalFilter: true,
            disableSortBy: true
        }
    ];

    //handles
    const handleClickInmuebleAdd = () => {
        showMessageModal({
            ...MESSAGE_MODALS.CREAR,
            onConfirm: () => CreateInmueble(),
        });
    };
    function CreateInmueble() {
        setState(prevState => ({ ...prevState, loading: true }));
        const callbackSuccess = (response) => {
            response.json()
                .then((row) => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    navigateWithFilters(`/inmueble/${OPERATION_MODE.EDIT}/${row.inmueble.id}`);
                })
                .catch((error) => {
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };
        ServerRequest(
            REQUEST_METHOD.POST, null, true,
            APIS.URLS.INMUEBLE, null,
            buildInmuebleEntityInit(),
            callbackSuccess, callbackNoSuccess, callbackError
        );
    }
    const handleClickInmuebleView = (id) => {
        navigateWithFilters('/inmueble/' + OPERATION_MODE.VIEW + '/' + id);
    };
    const handleClickInmuebleModify = (id) => {
        navigateWithFilters('/inmueble/' + OPERATION_MODE.EDIT + '/' + id);
    };
    const handleClickInmuebleRemove = (id) => {
        const inmueble = state.list.find(x => x.id === id);
        showMessageModal({
            ...MESSAGE_MODALS.BORRAR,
            message: `¿Está seguro de borrar el número de cuenta ${inmueble.numeroCuenta}?`,
            onConfirm: () => RemoveInmueble(id),
        });
    };
    const handleClickReactivar = (idCuenta, numeroCuenta) => {
        showMessageModal({
            title: "Confirmación",
            message: `¿Está seguro de reactivar el número de cuenta ${numeroCuenta}?`,
            onConfirm: () => ReactivarCuenta(idCuenta),
        });
    };
    const handleClickInmuebleDataTagger = (id) => {
        setState(prevState => ({ ...prevState, dataTagger: { showModal: true, idEntidad: id } }));
    };

    //funciones
    function SearchInmuebles() {
        return SearchInmueblesWithSavedFilters(getCurrentFilters());
    }

    function RemoveInmueble(id) {
        setState(prevState => ({ ...prevState, loading: true }));

        const callbackSuccess = (response) => {
            response.json()
                .then(() => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    SearchInmuebles();
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.INMUEBLE,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    function ReactivarCuenta(idCuenta) {
        setState(prevState => ({ ...prevState, loading: true }));

        const callbackSuccess = (response) => {
            response.json()
                .then(() => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'La cuenta fue reactivada correctamente');
                    SearchInmuebles();
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };

        const paramsUrl = `/reactivar/${idCuenta}`;

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.CUENTA,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    const dataTaggerRow = state.list.find(x => x.id === state.dataTagger.idEntidad)
    const dataTaggerRegistro = dataTaggerRow ? (dataTaggerRow.direccion ? `el inmueble de ${dataTaggerRow.direccion}` : `la cuenta ${dataTaggerRow.numeroCuenta}`) : ''

    return (
        <>
            <Loading visible={state.loading} />

            {state.dataTagger.showModal && (
                <DataTaggerModalApi
                    title="Información adicional de inmueble"
                    registro={dataTaggerRegistro}
                    tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_INMUEBLE_MODAL}
                    entidad="Inmueble"
                    idEntidad={state.dataTagger.idEntidad}
                    disabled={!canEdit}
                    onConfirm={() => {
                        setState(prevState => ({ ...prevState, dataTagger: { showModal: false, idEntidad: null } }));
                    }}
                    onDismiss={() => {
                        setState(prevState => ({ ...prevState, dataTagger: { showModal: false, idEntidad: null } }));
                    }}
                />
            )}

            <SectionHeading
                titles={[{ title: 'Tributos' }, { title: 'Administración de inmuebles' }]}
                onAddClick={canEdit ? handleClickInmuebleAdd : null}
                tableVisible={tableVisible}
            />

            <section className='section-accordion'>
                <WrapperAdvancedSearch
                    ref={searchRef}
                    onSearch={SearchInmuebles}
                    requireFilter={true}
                    message={searchMessage}
                    onClear={() => {
                        setState(prevState => ({ ...prevState, list: [] }));
                        setTableVisible(false);
                    }}
                    tabs={tabsConfig}
                    filters={[
                        { title: 'Número de cuenta', field: 'numeroCuenta', type: 'string', size: 3, searchOnEnter: true },
                        { title: 'Persona', field: 'idPersona', type: 'persona', size: 5 },
                        { title: 'Estado', field: 'idEstadoCuenta', type: 'selectLista', lista: 'EstadoCuenta', presetValue: 2, presetLabel: 'Activa', size: 2 },
                        { title: 'Etiqueta', field: 'etiqueta', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Dirección', field: 'direccion', type: 'string', size: 10, searchOnEnter: true },
                        { title: 'Número partida ARBA', field: 'catastralRtasPrv', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Condición especial', field: 'idsTipoCondicionEspecial', type: 'entityMultiple', entidad: 'TipoCondicionEspecial', filter: row => !row.idTipoTributo || row.idTipoTributo === TRIBUTO_TYPE.INMUEBLES, size: 6 },
                        { title: 'Recargo descuento', field: 'idsTipoRecargoDescuento', type: 'entityMultiple', entidad: 'TipoRecargoDescuento', size: 6 },
                        //Resolver label
                        { title: 'Nomenclatura Catastral', field: '', type: 'label', size: 12 },
                        { title: 'Partido', field: 'catastralPartido', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Cir.', field: 'catastralCir', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Sec.', field: 'catastralSec', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Código', field: 'catastralCodigo', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Chacra', field: 'catastralChacra', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'L.Chacra', field: 'catastralLchacra', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Quinta', field: 'catastralQuinta', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'L.Quinta', field: 'catastralLquinta', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Frac.', field: 'catastralFrac', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'L.Frac.', field: 'catastralLfrac', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Manz.', field: 'catastralManz', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'L.Manz.', field: 'catastralLmanz', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Parc.', field: 'catastralParc', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'L.Parc.', field: 'catastralLparc', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'SubParc.', field: 'catastralSubparc', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'U.Func.', field: 'catastralUfunc', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'U.Comp.', field: 'catastralUcomp', type: 'string', size: 2, searchOnEnter: true }
                    ]}
                />

                {tableVisible && (
                    <TableCustom
                        ref={tableRef}
                        className={'TableCustomBase m-top-20'}
                        columns={tableColumns}
                        data={state.list}
                        restoredPageIndex={restoredPageIndex}
                        messageEmpty="No se encontraron resultados"
                    />
                )}

            </section>
        </>
    );
}

export default InmueblesView;
